import {
  searchPeriodRange,
  useAuthenticatedUser,
  useOrganizationUsers,
} from '@airship-platform/airship-patterns';
import {
  EmptyState,
  SectionLoader,
} from '@airship-platform/jam-web/components';
import { zodResolver } from '@hookform/resolvers/zod';
import Box from '@mui/material/Box';
import { useEffect, useMemo, useRef } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useAppointmentHistory } from '../../../data-access/appointmentHistory';
import type { AppointmentHistoryFilter } from '../../../data-access/appointmentHistory/appointmentHistory.models';
import { useProposalEngagement } from '../../../data-access/proposalEngagement';
import AppointmentSearchForm, {
  AppointmentHistoryFormInputs,
  AppointmentHistoryFormSchema,
} from './forms/AppointmentSearchForm';
import AppointmentSearchList from './forms/AppointmentSearchList';

type AppointmentHistoryWatch = Pick<
  AppointmentHistoryFilter,
  | 'searchPeriod'
  | 'status'
  | 'searchTerm'
  | 'sorting'
  | 'proposalStatus'
  | 'startDateTime'
  | 'endDateTime'
  | 'includeNonAirshipJobs'
  | 'assignedUserIds'
>;

const AppointmentHistoryView: React.FC = () => {
  const filtersRef = useRef<AppointmentHistoryWatch | null>(null);

  const {
    appointmentHistory,
    isLoading,
    isLoaded,
    error,
    currentAppointmentFilter,
    updateHistoryFilter,
    fetchAppointmentHistory,
  } = useAppointmentHistory();
  const { fetchUsers } = useOrganizationUsers();
  const { user } = useAuthenticatedUser();
  const { fetchProposalEngagements } = useProposalEngagement();

  const appointmentSearchForm = useForm<AppointmentHistoryFormInputs>({
    defaultValues: {
      searchPeriod:
        user?.userRole === 'TECHNICIAN'
          ? 'LAST_30_DAYS'
          : currentAppointmentFilter.searchPeriod,
      status: currentAppointmentFilter.status,
      proposalStatus: currentAppointmentFilter.proposalStatus,
      searchTerm: currentAppointmentFilter.searchTerm,
      sorting: currentAppointmentFilter.sorting,
      includeNonAirshipJobs: currentAppointmentFilter.includeNonAirshipJobs,
      assignedUserIds: currentAppointmentFilter.assignedUserIds,
    },
    resolver: zodResolver(AppointmentHistoryFormSchema),
  });

  const searchPeriod = appointmentSearchForm.watch('searchPeriod');
  const status = appointmentSearchForm.watch('status');
  const searchTerm = appointmentSearchForm.watch('searchTerm');
  const sorting = appointmentSearchForm.watch('sorting');
  const proposalStatus = appointmentSearchForm.watch('proposalStatus');
  const includeNonAirshipJobs = appointmentSearchForm.watch(
    'includeNonAirshipJobs'
  );
  const assignedUserIds = appointmentSearchForm.watch('assignedUserIds');

  useEffect(() => {
    const { startDate, endDate } = searchPeriodRange(searchPeriod);

    const nextFilter: AppointmentHistoryWatch = {
      searchPeriod,
      status,
      proposalStatus,
      startDateTime: startDate.toISOString(),
      endDateTime: endDate.toISOString(),
      sorting,
      searchTerm,
      includeNonAirshipJobs,
      assignedUserIds,
    };

    const needsFetch =
      nextFilter.searchPeriod !== filtersRef.current?.searchPeriod;

    const appendsFilter =
      JSON.stringify({
        startDateTime: nextFilter.startDateTime,
        endDateTime: nextFilter.endDateTime,
        sorting: nextFilter.sorting,
        searchTerm: nextFilter.searchTerm,
        proposalStatus: nextFilter.proposalStatus,
        includeNonAirshipJobs: nextFilter.includeNonAirshipJobs,
        assignedUserIds: nextFilter.assignedUserIds,
      }) !==
      JSON.stringify({
        startDateTime: filtersRef.current?.startDateTime,
        endDateTime: filtersRef.current?.endDateTime,
        sorting: filtersRef.current?.sorting,
        searchTerm: filtersRef.current?.searchTerm,
        proposalStatus: filtersRef.current?.proposalStatus,
        includeNonAirshipJobs: filtersRef.current?.includeNonAirshipJobs,
        assignedUserIds: filtersRef.current?.assignedUserIds,
      });

    filtersRef.current = nextFilter;

    if (needsFetch) {
      fetchAppointmentHistory(nextFilter);
    }

    if (appendsFilter) {
      updateHistoryFilter(nextFilter);
    }
  }, [
    assignedUserIds,
    fetchAppointmentHistory,
    includeNonAirshipJobs,
    proposalStatus,
    searchPeriod,
    searchTerm,
    sorting,
    status,
    updateHistoryFilter,
  ]);

  useEffect(() => {
    const organizationId =
      user?.userRole !== 'TECHNICIAN' && user?.organizationId;
    if (organizationId) {
      fetchUsers(organizationId, {
        userRoles: ['TECHNICIAN'],
        status: ['ACTIVE'],
      });
    }
  }, [fetchUsers, user?.userRole, user?.organizationId]);

  const sharedProposalRequests = useMemo(
    () =>
      appointmentHistory.flatMap((appointment) =>
        appointment.proposals
          .filter((proposal) => proposal.hasBeenShared)
          .map((proposal) => ({
            appointmentId: appointment.appointmentId,
            appointmentProposalId: proposal.appointmentProposalId,
          }))
      ),
    [appointmentHistory]
  );

  const sharedProposalsKey = useMemo(
    () =>
      sharedProposalRequests
        .map((request) => request.appointmentProposalId)
        .sort()
        .join(','),
    [sharedProposalRequests]
  );

  const sharedProposalRequestsRef = useRef(sharedProposalRequests);
  sharedProposalRequestsRef.current = sharedProposalRequests;

  useEffect(() => {
    fetchProposalEngagements(sharedProposalRequestsRef.current);
  }, [sharedProposalsKey, fetchProposalEngagements]);

  const renderLoading = () => {
    if (isLoading && appointmentHistory.length === 0) {
      return (
        <SectionLoader isLoading title={'Fetching previous proposals...'} />
      );
    }
    if (error) {
      return (
        <EmptyState
          title={'Oops, there was an error'}
          subtitle={'Please contact support'}
        />
      );
    }
    if (!appointmentHistory.length) {
      return (
        <EmptyState
          title={'No proposals found'}
          subtitle={'Please update your search terms'}
        />
      );
    }
  };

  return (
    <Box
      component={'form'}
      noValidate
      autoComplete="off"
      sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}
    >
      <FormProvider {...appointmentSearchForm}>
        <AppointmentSearchForm isLoading={isLoading} />
      </FormProvider>

      {renderLoading()}

      {isLoaded && (
        <AppointmentSearchList
          appointments={appointmentHistory}
          pageSize={currentAppointmentFilter.pageSize}
          onPageSizeChange={updateHistoryFilter}
          flexDirection={'column'}
          gap={3}
        />
      )}
    </Box>
  );
};

export default AppointmentHistoryView;
